import {
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "src/firebase";
import { PaymentTransaction } from "src/features/payment-transactions/types/payment-transaction.entity";
import { UserSubscription } from "src/types/subscription.types";
import {
  BillingException,
  BillingExceptionIssueCode,
  BillingExceptionsPage,
  BillingExceptionScope,
  BillingExceptionUser,
} from "./types/billing-exception.entity";

const PAGE_SIZE = 10;
const SOURCE_BATCH_SIZE = PAGE_SIZE + 1;
const PENDING_CHECKOUT_THRESHOLD_IN_MS = 60 * 60 * 1000;
const USER_ID_CHUNK_SIZE = 10;

type UserRoutingData = {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  activeSubscriptionId?: string;
  appMeta?: {
    isSessionTemporarilyDisabled?: boolean;
    subscriptionExpireActionPending?: boolean;
  };
};

type BillingExceptionUserData = BillingExceptionUser & {
  isSubscriptionExpiryDeferred: boolean;
};

type OverdueSubscription = {
  id: string;
  userId: string;
  subscription: UserSubscription;
  sourceCursor: string;
};

type PaginatedBillingException = BillingException & {
  sourceCursor: string;
};

class BillingExceptionsRepository {
  private chunkUserIds(userIds: string[]) {
    const chunks: string[][] = [];

    for (let index = 0; index < userIds.length; index += USER_ID_CHUNK_SIZE) {
      chunks.push(userIds.slice(index, index + USER_ID_CHUNK_SIZE));
    }

    return chunks;
  }

  private async findUsers(userIds: string[]) {
    const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
    if (!uniqueUserIds.length) return new Map<string, BillingExceptionUserData>();

    const snapshots = await Promise.all(
      this.chunkUserIds(uniqueUserIds).map((ids) =>
        getDocs(query(collection(db, "users"), where(documentId(), "in", ids)))
      )
    );
    const users = new Map<string, BillingExceptionUserData>();

    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach((userDoc) => {
        const user = userDoc.data() as UserRoutingData;
        const name = user.name?.trim() || [user.firstName, user.lastName].filter(Boolean).join(" ") || userDoc.id;

        users.set(userDoc.id, {
          name,
          email: user.email,
          activeSubscriptionId: user.activeSubscriptionId,
          isSubscriptionExpiryDeferred:
            user.appMeta?.isSessionTemporarilyDisabled === true &&
            user.appMeta.subscriptionExpireActionPending === true,
        });
      });
    });

    return users;
  }

  private addMissingUserIssue(
    issueCodes: BillingExceptionIssueCode[],
    user: BillingExceptionUser | undefined
  ): BillingExceptionIssueCode[] {
    return user ? issueCodes : [...issueCodes, "USER_RECORD_MISSING"];
  }

  private async findPaymentCursor(offset?: string) {
    if (!offset) return undefined;

    const paymentDoc = await getDoc(doc(db, "payment_transactions", offset));
    return paymentDoc.exists() ? paymentDoc : undefined;
  }

  private encodeSubscriptionCursor(userId: string, subscriptionId: string) {
    return `${userId}::${subscriptionId}`;
  }

  private async findSubscriptionCursor(offset?: string) {
    if (!offset) return undefined;

    const [userId, subscriptionId] = offset.split("::");
    if (!userId || !subscriptionId) return undefined;

    const subscriptionDoc = await getDoc(doc(db, "users", userId, "user_subscriptions", subscriptionId));
    return subscriptionDoc.exists() ? subscriptionDoc : undefined;
  }

  async find(scope: BillingExceptionScope, offset?: string): Promise<BillingExceptionsPage> {
    return scope === "payments" ? this.findUncompletedCheckouts(offset) : this.findOverdueSubscriptions(offset);
  }

  private async findUncompletedCheckouts(offset?: string): Promise<BillingExceptionsPage> {
    const threshold = Timestamp.fromMillis(Date.now() - PENDING_CHECKOUT_THRESHOLD_IN_MS);
    const cursor = await this.findPaymentCursor(offset);
    const paymentSnapshot = await getDocs(
      query(
        collection(db, "payment_transactions"),
        where("paymentStatus", "==", "INITIALIZED"),
        where("createdAt", "<=", threshold),
        orderBy("createdAt", "desc"),
        ...(cursor ? [startAfter(cursor)] : []),
        limit(SOURCE_BATCH_SIZE)
      )
    );
    const paymentDocs = paymentSnapshot.docs.slice(0, PAGE_SIZE);
    const payments = paymentDocs.map((paymentDoc) => ({
      id: paymentDoc.id,
      ...(paymentDoc.data() as PaymentTransaction),
    }));
    const users = await this.findUsers(payments.map(({ userId }) => userId));

    return {
      items: payments.map((payment) => {
        const user = users.get(payment.userId);

        return {
          id: `payment:${payment.id}`,
          scope: "payments",
          issueCodes: this.addMissingUserIssue(["CHECKOUT_NOT_COMPLETED"], user),
          userId: payment.userId,
          user,
          recordId: payment.id,
          payment,
          detectedAt: payment.createdAt,
          sourceCursor: payment.id,
        };
      }),
      nextOffset: paymentSnapshot.size > PAGE_SIZE ? paymentDocs[paymentDocs.length - 1]?.id : undefined,
    };
  }

  private isCurrentGracePeriod(subscription: UserSubscription, now: Timestamp) {
    return subscription.isInGracePeriod === true && (subscription.graceUntil?.toMillis() ?? 0) > now.toMillis();
  }

  private makeOverdueSubscriptionException(
    overdueSubscription: OverdueSubscription,
    user: BillingExceptionUserData | undefined,
    now: Timestamp
  ): PaginatedBillingException | undefined {
    if (this.isCurrentGracePeriod(overdueSubscription.subscription, now)) return undefined;
    if (user?.isSubscriptionExpiryDeferred) return undefined;

    const issueCodes: BillingExceptionIssueCode[] = ["ACTIVE_SUBSCRIPTION_PAST_END_DATE"];

    if (user && user.activeSubscriptionId !== overdueSubscription.id) {
      issueCodes.push("ACTIVE_SUBSCRIPTION_POINTER_MISMATCH");
    }

    return {
      id: `subscription:${overdueSubscription.userId}:${overdueSubscription.id}`,
      scope: "subscriptions",
      issueCodes: this.addMissingUserIssue(issueCodes, user),
      userId: overdueSubscription.userId,
      user,
      recordId: overdueSubscription.id,
      subscription: overdueSubscription.subscription,
      detectedAt: overdueSubscription.subscription.endDate,
      sourceCursor: overdueSubscription.sourceCursor,
    };
  }

  private async findOverdueSubscriptions(offset?: string): Promise<BillingExceptionsPage> {
    const now = Timestamp.now();
    const items: PaginatedBillingException[] = [];
    let cursor = await this.findSubscriptionCursor(offset);

    // Scan source batches only until this page and its next-page probe are known.
    while (true) {
      const subscriptionSnapshot = await getDocs(
        query(
          collectionGroup(db, "user_subscriptions"),
          where("status", "==", "ACTIVE"),
          where("endDate", "<=", now),
          orderBy("endDate", "asc"),
          ...(cursor ? [startAfter(cursor)] : []),
          limit(SOURCE_BATCH_SIZE)
        )
      );
      if (subscriptionSnapshot.empty) return { items };

      const overdueSubscriptions = subscriptionSnapshot.docs
        .map((subscriptionDoc) => {
          const userId = subscriptionDoc.ref.parent.parent?.id;
          if (!userId) return undefined;

          return {
            id: subscriptionDoc.id,
            userId,
            subscription: subscriptionDoc.data() as UserSubscription,
            sourceCursor: this.encodeSubscriptionCursor(userId, subscriptionDoc.id),
          };
        })
        .filter(
          (subscription): subscription is OverdueSubscription => Boolean(subscription)
        );
      const users = await this.findUsers(overdueSubscriptions.map(({ userId }) => userId));

      for (const overdueSubscription of overdueSubscriptions) {
        const exception = this.makeOverdueSubscriptionException(
          overdueSubscription,
          users.get(overdueSubscription.userId),
          now
        );
        if (!exception) continue;

        items.push(exception);

        if (items.length > PAGE_SIZE) {
          return {
            items: items.slice(0, PAGE_SIZE),
            nextOffset: items[PAGE_SIZE - 1].sourceCursor,
          };
        }
      }

      if (subscriptionSnapshot.size < SOURCE_BATCH_SIZE) return { items };
      cursor = subscriptionSnapshot.docs[subscriptionSnapshot.docs.length - 1];
    }
  }
}

export const billingExceptionsRepository = new BillingExceptionsRepository();
